/**
 * The Electron (desktop) disk-backup target, over the preload IPC bridge.
 *
 * WHY IT NEVER TOUCHES THE FILESYSTEM DIRECTLY
 * --------------------------------------------
 * The renderer has no `fs`. Every operation is an allow-listed `invoke` that
 * names a file INSIDE the folder the user picked; the main process resolves the
 * path and refuses anything that escapes it. This adapter therefore deals only
 * in bare filenames — it has no notion of a path, and cannot be tricked into
 * writing one.
 *
 * THE BRIDGE IS OPTIONAL, DETECTED BY CAPABILITY
 * ----------------------------------------------
 * Exactly like the print port: if `window.dnr.backupAppend` is absent, this
 * target reports `supported: false`, so one bundle serves both browser and
 * desktop and a stale preload degrades to "not supported" rather than a crash.
 *
 * ERROR MAPPING
 * -------------
 * The main process returns `{ ok: false, error }` with an `ENOENT`/`ENOSPC`
 * derived code rather than throwing, because a thrown error across the IPC
 * boundary loses its shape. This adapter maps those codes back onto the same
 * `DiskBackupError` codes the browser target throws, so the engine's status and
 * retry logic are identical on both platforms.
 */

import { DiskBackupError, type DiskBackupTarget, type BackupReadiness } from './diskBackupTarget';

interface ElectronBackupBridge {
  backupPickFolder(): Promise<{ picked: boolean; label?: string }>;
  backupFolder(): Promise<{ picked: boolean; label?: string }>;
  backupRead(name: string): Promise<{ ok: boolean; text?: string; error?: string }>;
  backupAppend(name: string, text: string): Promise<{ ok: boolean; error?: string }>;
  backupWriteAtomic(name: string, text: string): Promise<{ ok: boolean; error?: string }>;
  backupList(): Promise<{ ok: boolean; names?: string[]; error?: string }>;
}

export function electronBackupBridge(): ElectronBackupBridge | undefined {
  const g = globalThis as { dnr?: Partial<ElectronBackupBridge> };
  const d = g.dnr;
  return typeof d?.backupAppend === 'function' && typeof d?.backupWriteAtomic === 'function'
    ? (d as ElectronBackupBridge)
    : undefined;
}

function toError(code: string | undefined, fallback: string): DiskBackupError {
  switch (code) {
    case 'ENOENT':
    case 'NOT_FOUND':
      return new DiskBackupError('NOT_FOUND', fallback);
    case 'ENOSPC':
    case 'DISK_FULL':
      return new DiskBackupError('DISK_FULL', 'The backup disk is full. Free up space and try again.');
    case 'EACCES':
    case 'PERMISSION_DENIED':
      return new DiskBackupError('PERMISSION_DENIED', 'Permission to write to the backup folder was denied.');
    default:
      return new DiskBackupError('IO_ERROR', fallback);
  }
}

export class ElectronDiskBackupTarget implements DiskBackupTarget {
  private readonly bridge: ElectronBackupBridge;
  private label = '';

  constructor(bridge: ElectronBackupBridge = electronBackupBridge() as ElectronBackupBridge) {
    this.bridge = bridge;
  }

  get supported(): boolean {
    return this.bridge !== undefined;
  }

  async pickFolder(): Promise<string> {
    const result = await this.bridge.backupPickFolder();
    if (!result.picked) {
      throw new DiskBackupError('PERMISSION_DENIED', 'No folder was selected.');
    }
    this.label = result.label ?? 'Backup folder';
    return this.label;
  }

  async ensurePermission(): Promise<boolean> {
    // On desktop the folder is chosen per-session in the main process; there is
    // no revocable web permission. If none is chosen yet, report false so the UI
    // asks the user to pick one — never throw, because this is the normal
    // first-run state rather than a revocation.
    const result = await this.bridge.backupFolder();
    if (result.picked) {
      this.label = result.label ?? this.label ?? 'Backup folder';
      return true;
    }
    return false;
  }

  /**
   * Non-blocking readiness check for the automatic-backup scheduler.
   *
   * On Electron, `backupFolder` simply asks the main process whether a folder
   * is set — no user gesture is required. If a folder is set, the IPC bridge
   * has persistent filesystem access, so 'ready' is safe to report.
   */
  async checkReady(): Promise<BackupReadiness> {
    const result = await this.bridge.backupFolder();
    return result.picked ? 'ready' : 'no_folder';
  }

  async readFile(name: string): Promise<string | null> {
    const result = await this.bridge.backupRead(name);
    if (result.ok) return result.text ?? '';
    if (result.error === 'NOT_FOUND' || result.error === 'ENOENT') return null;
    throw toError(result.error, `Could not read ${name} from the backup folder.`);
  }

  async appendFile(name: string, text: string): Promise<void> {
    const result = await this.bridge.backupAppend(name, text);
    if (!result.ok) throw toError(result.error, `Could not append to ${name}.`);
  }

  async writeFileAtomic(name: string, text: string): Promise<void> {
    const result = await this.bridge.backupWriteAtomic(name, text);
    if (!result.ok) throw toError(result.error, `Could not write ${name}.`);
  }

  async listFiles(): Promise<readonly string[]> {
    const result = await this.bridge.backupList();
    return result.names ?? [];
  }

  folderLabel(): string {
    return this.label || 'Backup folder';
  }
}
